import type { LineOverrideLike } from './bookStyleCss';
import { DEFAULT_BASE_FONT_SIZE_PX } from './bookDefaults';

export type LineStyleFormValues = {
  fontSize?: number;
  color?: string;
  backgroundColor?: string;
  fontFamily?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  align?: 'left' | 'center' | 'right' | 'justify';
  span?: 12 | 24;
  borderWidth?: number;
  borderStyle?: string;
  borderColor?: string;
  borderRadius?: number;
  padding?: number;
  paddingLeft?: number;
  paddingRight?: number;
  paddingTop?: number;
  paddingBottom?: number;
  margin?: number;
  marginLeft?: number;
  marginRight?: number;
  marginTop?: number;
  marginBottom?: number;
  lineHeight?: number;
  textIndent?: number;
  widthPercent?: number;
  display?: '' | 'block' | 'inline' | 'inline-block';
};

export const LINE_STYLE_FORM_DEFAULTS: LineStyleFormValues = {
  fontSize: DEFAULT_BASE_FONT_SIZE_PX,
  color: '',
  backgroundColor: '',
  fontFamily: '',
  bold: false,
  italic: false,
  underline: false,
  align: 'left',
  span: 24,
  borderWidth: 0,
  borderStyle: 'none',
  borderColor: '',
  borderRadius: 0,
  padding: 0,
  paddingLeft: 0,
  paddingRight: 0,
  paddingTop: 0,
  paddingBottom: 0,
  margin: 0,
  marginLeft: 0,
  marginRight: 0,
  marginTop: 0,
  marginBottom: 0,
  lineHeight: 1,
  textIndent: 0,
  widthPercent: 0,
  display: '',
};

function positiveNum(v: unknown): number | undefined {
  if (v == null || v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function signedNum(v: unknown): number | undefined {
  if (v == null || v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) && n !== 0 ? n : undefined;
}

function lineHeightNum(v: unknown): number | undefined {
  if (v == null || v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

export function parseWidthPercent(width?: string | number): number {
  if (typeof width === 'string' && width.trim().endsWith('%')) {
    const n = parseFloat(width);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

export function widthPercentToCss(widthPercent?: number): string | undefined {
  const n = Number(widthPercent ?? 0);
  if (!Number.isFinite(n) || n <= 0 || n > 100) return undefined;
  return `${n}%`;
}

export function lineOverrideToFormValues(
  override: LineOverrideLike | undefined,
  defaults?: Partial<LineStyleFormValues>,
): LineStyleFormValues {
  const base = { ...LINE_STYLE_FORM_DEFAULTS, ...defaults };
  if (!override) return base;

  return {
    ...base,
    fontSize: override.fontSize ?? base.fontSize,
    color: override.color ?? base.color,
    backgroundColor: override.backgroundColor ?? base.backgroundColor,
    fontFamily: override.fontFamily ?? base.fontFamily,
    bold: (override.fontWeight ?? 400) >= 600,
    italic: override.fontStyle === 'italic',
    underline: (override.textDecoration ?? '').includes('underline'),
    align: override.textAlign ?? base.align,
    borderWidth: override.borderWidth ?? base.borderWidth,
    borderStyle: override.borderStyle ?? base.borderStyle,
    borderColor: override.borderColor ?? base.borderColor,
    borderRadius: override.borderRadius ?? base.borderRadius,
    padding: override.padding ?? base.padding,
    paddingLeft: override.paddingLeft ?? base.paddingLeft,
    paddingRight: override.paddingRight ?? base.paddingRight,
    paddingTop: override.paddingTop ?? base.paddingTop,
    paddingBottom: override.paddingBottom ?? base.paddingBottom,
    margin: override.margin ?? base.margin,
    marginLeft: override.marginLeft ?? base.marginLeft,
    marginRight: override.marginRight ?? base.marginRight,
    marginTop: override.marginTop ?? base.marginTop,
    marginBottom: override.marginBottom ?? base.marginBottom,
    lineHeight: override.lineHeight ?? base.lineHeight,
    textIndent: override.textIndent ?? base.textIndent,
    widthPercent: parseWidthPercent(override.width) || base.widthPercent,
    display: override.display ?? base.display,
    span:
      (override as { span?: unknown }).span === 12 || (override as { span?: unknown }).span === 24
        ? ((override as { span: 12 | 24 }).span as 12 | 24)
        : base.span,
  };
}

export function formValuesToLineOverride(
  values: Record<string, unknown>,
  opts?: { content?: string },
): LineOverrideLike {
  const width = widthPercentToCss(values.widthPercent as number | undefined);
  const displayRaw = values.display;
  const display =
    displayRaw === 'block' || displayRaw === 'inline' || displayRaw === 'inline-block'
      ? displayRaw
      : undefined;
  const out: LineOverrideLike = {
    ...(opts?.content != null ? { content: opts.content } : {}),
    fontSize: values.fontSize != null ? Number(values.fontSize) : undefined,
    color: typeof values.color === 'string' && values.color.trim() ? values.color.trim() : undefined,
    backgroundColor:
      typeof values.backgroundColor === 'string' && values.backgroundColor.trim()
        ? values.backgroundColor.trim()
        : undefined,
    fontFamily: typeof values.fontFamily === 'string' && values.fontFamily.trim() ? values.fontFamily.trim() : undefined,
    fontWeight:
      values.fontWeight != null && values.fontWeight !== ''
        ? Number(values.fontWeight)
        : values.bold
          ? 700
          : 400,
    fontStyle: values.italic ? 'italic' : 'normal',
    textDecoration: values.underline ? 'underline' : undefined,
    textAlign: values.align as LineOverrideLike['textAlign'],
    borderWidth: positiveNum(values.borderWidth),
    borderStyle:
      values.borderStyle && values.borderStyle !== 'none'
        ? (values.borderStyle as LineOverrideLike['borderStyle'])
        : undefined,
    borderColor: typeof values.borderColor === 'string' && values.borderColor.trim() ? values.borderColor.trim() : undefined,
    borderRadius: positiveNum(values.borderRadius),
    padding: positiveNum(values.padding),
    paddingLeft: positiveNum(values.paddingLeft),
    paddingRight: positiveNum(values.paddingRight),
    paddingTop: positiveNum(values.paddingTop),
    paddingBottom: positiveNum(values.paddingBottom),
    margin: positiveNum(values.margin),
    marginLeft: positiveNum(values.marginLeft),
    marginRight: positiveNum(values.marginRight),
    marginTop: positiveNum(values.marginTop),
    marginBottom: positiveNum(values.marginBottom),
    lineHeight: lineHeightNum(values.lineHeight),
    textIndent: signedNum(values.textIndent),
    width,
    display,
  };

  return out;
}

/** Convert tag style form draft to stored override (delta only; merges with existing on flush). */
export function tagStyleDraftToStoredOverride(
  draft: Record<string, unknown>,
): LineOverrideLike & { span?: 12 | 24 } {
  const out = formValuesToStyleOnlyOverride(draft);
  const span = draft.span === 12 || draft.span === 24 ? draft.span : undefined;
  return span ? { ...out, span } : out;
}

/** Style fields controlled by line-style forms — replaced wholesale on apply. */
export const FORM_STYLE_FIELD_KEYS = [
  'fontSize',
  'color',
  'backgroundColor',
  'fontFamily',
  'fontWeight',
  'fontStyle',
  'textDecoration',
  'textAlign',
  'borderWidth',
  'borderStyle',
  'borderColor',
  'borderRadius',
  'padding',
  'paddingLeft',
  'paddingRight',
  'paddingTop',
  'paddingBottom',
  'margin',
  'marginLeft',
  'marginRight',
  'marginTop',
  'marginBottom',
  'lineHeight',
  'textIndent',
  'width',
  'display',
] as const satisfies readonly (keyof LineOverrideLike)[];

type PreservedOverrideFields = {
  content?: string;
  objectFit?: string;
};

/** Style-only override for bulk apply — omits default bold/normal/line-height noise. */
export function formValuesToStyleOnlyOverride(values: Record<string, unknown>): LineOverrideLike {
  const out = formValuesToLineOverride(values);
  if (!values.bold && (values.fontWeight == null || values.fontWeight === '')) {
    delete out.fontWeight;
  }
  if (!values.italic) {
    delete out.fontStyle;
  }
  if (!values.underline) {
    delete out.textDecoration;
  }
  const lineHeight = Number(values.lineHeight ?? LINE_STYLE_FORM_DEFAULTS.lineHeight);
  if (!Number.isFinite(lineHeight) || lineHeight === LINE_STYLE_FORM_DEFAULTS.lineHeight) {
    delete out.lineHeight;
  }
  return out;
}

/** Apply form styles to an override, clearing fields the form reset to empty/default. */
export function mergeFormStylesIntoOverride(
  prev: LineOverrideLike & PreservedOverrideFields,
  values: Record<string, unknown>,
): LineOverrideLike & PreservedOverrideFields {
  const fromForm = formValuesToLineOverride(values);
  const next: LineOverrideLike & PreservedOverrideFields = {};

  if (prev.content != null && prev.content !== '') {
    next.content = prev.content;
  }
  if (prev.objectFit != null && prev.objectFit !== '') {
    next.objectFit = prev.objectFit;
  }

  for (const key of FORM_STYLE_FIELD_KEYS) {
    const value = fromForm[key];
    if (value !== undefined) {
      Object.assign(next, { [key]: value });
    }
  }

  return next;
}

/** Bulk apply (select / tag) — omits default bold/normal/line-height noise. */
export function mergeBulkFormStylesIntoOverride(
  prev: LineOverrideLike & PreservedOverrideFields,
  values: Record<string, unknown>,
): LineOverrideLike & PreservedOverrideFields {
  const fromForm = formValuesToStyleOnlyOverride(values);
  const fullForm = formValuesToLineOverride(values);
  const next: LineOverrideLike & PreservedOverrideFields = {};

  if (prev.content != null && prev.content !== '') {
    next.content = prev.content;
  }
  if (prev.objectFit != null && prev.objectFit !== '') {
    next.objectFit = prev.objectFit;
  }

  const preferStyleOnlyOnly = new Set<keyof LineOverrideLike>([
    'fontWeight',
    'fontStyle',
    'textDecoration',
    'lineHeight',
  ]);

  for (const key of FORM_STYLE_FIELD_KEYS) {
    const value =
      fromForm[key] ?? (preferStyleOnlyOnly.has(key) ? undefined : fullForm[key]);
    if (value !== undefined) {
      Object.assign(next, { [key]: value });
    }
  }

  return next;
}
